import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  useNotify,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stListRooms,
  stPoolDashboard,
  StPoolDashboard,
  StSettlementCycle,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { StIdCell, StStatusChip, stText } from '../StListUi';
import './init';
import { DualPoolProgressCell } from './DualPoolProgressCell';

function formatAmount(v?: string | number | null): string {
  if (v == null || v === '') return '0';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

const ListFilter = (props: any) => {
  const t = useTranslate();
  const [roomChoices, setRoomChoices] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await stListRooms({ limit: 200, offset: 0 });
        if (cancelled) return;
        setRoomChoices(
          (res?.items || []).map((r) => ({
            id: r.id,
            name: `${r.name} (${r.type})`,
          }))
        );
      } catch {
        if (!cancelled) setRoomChoices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Filter {...(props || {})}>
      <SelectInput
        source="status"
        label={t('module.socialTrading.colStatus')}
        choices={[
          { id: 'UPCOMING', name: 'UPCOMING' },
          { id: 'ACTIVE', name: 'ACTIVE' },
          { id: 'PENDING_SETTLEMENT', name: 'PENDING_SETTLEMENT' },
          { id: 'SETTLED', name: 'SETTLED' },
          { id: 'ENDED_UNQUALIFIED', name: 'ENDED_UNQUALIFIED' },
        ]}
        alwaysOn
        resettable
      />
      <SelectInput
        source="roomId"
        label={t('module.socialTrading.colRoom')}
        choices={roomChoices}
        alwaysOn
        resettable
      />
    </Filter>
  );
};

export default function SocialTradingPoolDashboardList() {
  const t = useTranslate();
  const notify = useNotify();
  const [dash, setDash] = useState<StPoolDashboard | null>(null);

  const load = useCallback(async () => {
    try {
      setDash(await stPoolDashboard());
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = dash
    ? [
        {
          title: t('module.socialTrading.dashTotalPools'),
          value: dash.totalPools,
          note: t('module.socialTrading.dashTotalPoolsNote'),
        },
        {
          title: t('module.socialTrading.dashUpcomingPools'),
          value: dash.upcomingPools,
          note: 'UPCOMING',
        },
        {
          title: t('module.socialTrading.dashActivePools'),
          value: dash.activePools,
          note: 'ACTIVE',
        },
        {
          title: t('module.socialTrading.dashEndedPools'),
          value: dash.endedPools,
          note: 'ENDED',
        },
        {
          title: t('module.socialTrading.dashActiveEnrolledUsers'),
          value: dash.activeEnrolledUsers,
          note: t('module.socialTrading.dashActiveEnrolledUsersNote'),
        },
        {
          title: t('module.socialTrading.dashTotalVolume'),
          value: `${formatAmount(dash.totalVolume)} USDC`,
          note: t('module.socialTrading.dashAllCyclesNote'),
        },
        {
          title: t('module.socialTrading.dashTotalProfit'),
          value: `${formatAmount(dash.totalProfit)} USDC`,
          note: t('module.socialTrading.dashAllCyclesNote'),
        },
        {
          title: t('module.socialTrading.dashConfiguredReward'),
          value: `${formatAmount(dash.configuredRewardTotal)} USDC`,
          note: t('module.socialTrading.dashConfiguredRewardNote'),
        },
        {
          title: t('module.socialTrading.dashSettledReward'),
          value: `${formatAmount(dash.settledRewardTotal)} USDC`,
          note: t('module.socialTrading.dashSettledRewardNote'),
        },
      ]
    : [];

  return (
    <PermissionGuard resource="st-pool-dashboard" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.poolDashboard')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.poolDashboard.root },
            { name: t('module.socialTrading.poolDashboard') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          {t('module.socialTrading.poolDashboardHint')}
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {kpis.map((k) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={k.title}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {k.title}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 0.5 }}>
                      {k.value ?? '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {k.note}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ pb: 2 }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="h6">{t('module.socialTrading.settlementCycles')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.settlementCyclesHint')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('module.socialTrading.settlementCyclesClaimNote')}
              </Typography>
            </Box>
            <List
              resource={Resources.socialTradingSettlementCycles}
              actions={false}
              empty={false}
              perPage={10}
              exporter={false}
              filters={<ListFilter />}
              sx={{
                '& .RaFilter-form': {
                  px: 2,
                  pt: 2,
                  pb: 1,
                  columnGap: 2,
                  rowGap: 1.5,
                  alignItems: 'flex-end',
                },
              }}
            >
              <Box sx={{ overflow: 'auto' }}>
                <Datagrid rowClick={false} bulkActionButtons={false}>
                  <FunctionField
                    label={t('module.socialTrading.colCycle')}
                    render={(c: StSettlementCycle) => stText(c.cycleLabel)}
                  />
                  <FunctionField
                    label="cycleId"
                    render={(c: StSettlementCycle) => <StIdCell value={c.id} />}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colRoom')}
                    render={(c: StSettlementCycle) => stText(c.roomName)}
                  />
                  <FunctionField
                    label="roomId"
                    render={(c: StSettlementCycle) => <StIdCell value={c.roomId} />}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colVolumePool')}
                    render={(c: StSettlementCycle) => (
                      <DualPoolProgressCell
                        current={c.volumeCurrent}
                        target={c.volumeTarget}
                        reward={c.volumePoolAmount}
                        cycleStatus={c.status}
                      />
                    )}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colProfitPool')}
                    render={(c: StSettlementCycle) => (
                      <DualPoolProgressCell
                        current={c.profitCurrent}
                        target={c.profitTarget}
                        reward={c.profitPoolAmount}
                        cycleStatus={c.status}
                      />
                    )}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colEnrollments')}
                    render={(c: StSettlementCycle) => stText(c.participantCount)}
                  />
                  <FunctionField
                    label={t('module.socialTrading.colStatus')}
                    render={(c: StSettlementCycle) => <StStatusChip status={c.status} />}
                  />
                </Datagrid>
              </Box>
            </List>
          </Card>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
